/*
 * Copyright Mimic Networks, Inc. 2024.
 */

import {
  ApiOutlined,
  BellOutlined,
  BookOutlined,
  BuildOutlined,
  CommentOutlined,
  DashboardOutlined,
  DeploymentUnitOutlined,
  DownloadOutlined,
  LineChartOutlined,
  OneToOneOutlined,
  UnorderedListOutlined,
  FileTextOutlined,
} from '@ant-design/icons';

import { BetaBadge } from '@/components/menus/BetaBadge';
import { buildMenu, MenuItemConfig, MenuProps } from '@/components/menus/builder';
import { canPerformAction } from '@/hooks/useAuthorization';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import ExternalLink from '@/primitives/Icons/ExternalLink.svg?react';
import { Menu } from '@/primitives/Menu';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';
import { arePluginsEnabled, isDashcamEnabled } from '@/utils/environments';
import './MainMenu.css';

const hiddenOnSubtenants = ['notifications'];
const productionOnly = ['support'];
const pluginsOnly = ['extensibility'];
const dashcamOnly = ['dashcam'];

export function MainMenu({ tenantID, currentUser, currentPath }: MenuProps) {
  const { t } = useMimicTranslation('menu');
  const setMenuOpen = useMgmtPlaneStateStore((state) => state.setMenuOpen);
  const selectedSubtenantId = useMgmtPlaneStateStore((state) => state.selectedSubtenantID);

  const env = window.mimicConfig.MIMIC_MGMT_PLANE_ENVIRONMENT || 'dev';
  const dashcamSubdomain = window.mimicConfig.MIMIC_MGMT_PLANE_ENVIRONMENT === 'prod' ? 'auto-ir' : `auto-ir.${env}`;

  if (!currentUser) return null;

  const allMenuItems: MenuItemConfig[] = [
    {
      name: 'activity',
      icon: <OneToOneOutlined />,
      label: t('activity'),
      path: `/tenants/${tenantID}/activity`,
      dataTestId: 'activity-menu',
      requiredRole: 'viewer',
      closeMenuOnClick: true,
    },
    {
      name: 'nodes',
      icon: <DeploymentUnitOutlined />,
      label: t('nodes'),
      path: `/tenants/${tenantID}/nodes`,
      dataTestId: 'nodes-menu',
      requiredRole: 'viewer',
      closeMenuOnClick: true,
    },
    {
      name: 'configurations',
      icon: <BuildOutlined />,
      label: t('configurations'),
      path: `/tenants/${tenantID}/node-configs`,
      dataTestId: 'configurations-menu',
      requiredRole: 'admin',
      closeMenuOnClick: true,
    },
    {
      name: 'telemetry',
      icon: <LineChartOutlined />,
      labelRenderer: <BetaBadge testId="telemetry-beta-badge">{t('telemetry')}</BetaBadge>,
      path: `/tenants/${tenantID}/telemetry-viewer`,
      dataTestId: 'telemetry-menu',
      requiredRole: 'viewer',
      closeMenuOnClick: true,
    },
    {
      name: 'reports',
      icon: <FileTextOutlined />,
      labelRenderer: <BetaBadge testId="reports-beta-badge">{t('reports')}</BetaBadge>,
      dataTestId: 'reports-menu',
      items: [
        {
          name: 'driftReports',
          label: t('driftReports'),
          path: `/tenants/${tenantID}/drift-reports`,
          closeMenuOnClick: true,
        },
        {
          name: 'changeAudit',
          label: t('changeAudit'),
          path: `/tenants/${tenantID}/change-audit`,
          requiredRole: 'superadmin',
          closeMenuOnClick: true,
        },
        {
          name: 'biDashboards',
          label: t('biDashboards'),
          path: `/tenants/${tenantID}/bi-dashboard`,
          requiredRole: 'superadmin',
          closeMenuOnClick: true,
        },
      ],
      requiredRole: 'viewer',
      closeMenuOnClick: true,
    },
    {
      name: 'jobs',
      icon: <UnorderedListOutlined />,
      label: t('jobs'),
      path: `/tenants/${tenantID}/jobs`,
      dataTestId: 'jobs-menu',
      requiredRole: 'viewer',
      closeMenuOnClick: true,
    },
    {
      name: 'notifications',
      icon: <BellOutlined />,
      label: t('notifications'),
      path: `/tenants/${tenantID}/notifications/management`,
      dataTestId: 'notifications-menu',
      requiredRole: 'admin',
      closeMenuOnClick: true,
    },
    {
      name: 'dashcam',
      icon: <DashboardOutlined />,
      labelRenderer: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: 'transparent' }}>
          {t('dashcam')}
          <ExternalLink style={{ fontSize: '16px', marginBottom: '2px' }} />
        </div>
      ),
      onClick: () => {
        let url = `https://${dashcamSubdomain}.mimic.com?tenantId=${tenantID}`;
        if (selectedSubtenantId) {
          url += `&subtenantId=${selectedSubtenantId}`;
        }
        window.open(url, '_blank');
      },
      dataTestId: 'dashcam-menu',
      requiredRole: 'viewer',
      closeMenuOnClick: false,
    },
    {
      name: 'docs',
      icon: <BookOutlined />,
      label: t('docs'),
      items: [
        {
          name: 'apiReference',
          label: t('apiReference'),
          path: `/tenants/${tenantID}/docs/api-reference`,
          closeMenuOnClick: true,
        },
      ],
      requiredRole: 'admin',
    },
    {
      name: 'downloadNode',
      icon: <DownloadOutlined />,
      label: t('downloadNode'),
      dataTestId: 'download-node-menu',
      requiredRole: 'admin',
      items: [
        {
          name: 'nodeReleasesWindows',
          label: t('windows'),
          path: `/tenants/${tenantID}/node-releases/windows`,
          closeMenuOnClick: true,
        },
        {
          name: 'nodeReleasesLinux',
          label: t('linux'),
          path: `/tenants/${tenantID}/node-releases/linux`,
          closeMenuOnClick: true,
        },
        {
          name: 'installerTokens',
          label: t('installerTokens'),
          path: `/tenants/${tenantID}/installer-tokens`,
          dataTestId: 'installer-tokens-menu',
          closeMenuOnClick: true,
        },
        {
          name: 'tenantReleases',
          label: t('releaseManagement'),
          path: `/tenants/${tenantID}/release-management`,
          requiredRole: 'superadmin',
          closeMenuOnClick: true,
        },
      ],
    },
    {
      name: 'extensibility',
      icon: <ApiOutlined />,
      labelRenderer: <BetaBadge testId="extensibility-beta-badge">{t('extensibility')}</BetaBadge>,
      dataTestId: 'extensibility-menu',
      requiredRole: 'superadmin',
      items: [
        {
          name: 'nodePlugins',
          label: t('nodePlugins'),
          path: `/tenants/${tenantID}/node-plugins`,
          dataTestId: 'node-plugins-menu',
          closeMenuOnClick: true,
        },
      ],
    },
    {
      name: 'support',
      icon: <CommentOutlined />,
      labelRenderer: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: 'transparent' }}>
          {t('contactSupport')}
          <ExternalLink style={{ fontSize: '16px', marginBottom: '2px' }} />
        </div>
      ),
      onClick: () => {
        const url = `https://portal.mimic.support`;
        window.open(url, '_blank');
      },
      dataTestId: 'support-menu',
      requiredRole: 'viewer',
      closeMenuOnClick: false,
    },
  ];

  const filterByRole = (items: MenuItemConfig[]): MenuItemConfig[] =>
    items
      .filter((item) => !item.requiredRole || canPerformAction(item.requiredRole, currentUser))
      .map((item) => (item.items ? { ...item, items: filterByRole(item.items) } : item));

  const menu = filterByRole(
    allMenuItems.filter((item) => {
      // Hide items that are not relevant in subtenant context
      if (selectedSubtenantId && hiddenOnSubtenants.includes(item.name)) {
        return false;
      }
      // Hide items that are only available in production
      if (env !== 'prod' && productionOnly.includes(item.name)) {
        return false;
      }
      // Hide items that depend on the plugins feature when it's disabled in this env
      if (!arePluginsEnabled() && pluginsOnly.includes(item.name)) {
        return false;
      }
      // Dashcam is out of scope for gov customers (PLAT-1397)
      if (!isDashcamEnabled() && dashcamOnly.includes(item.name)) {
        return false;
      }
      return true;
    }),
  );
  const { items, defaultSelectedKeys, defaultOpenKeys } = buildMenu(menu, currentPath, setMenuOpen);
  return (
    <Menu items={items} key={currentPath} defaultSelectedKeys={defaultSelectedKeys} defaultOpenKeys={defaultOpenKeys} />
  );
}
